import { useMemo, useState, type ReactElement } from 'react';
import type { FreeformAnswer } from 'src/redux/survey/survey.types';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { PortalDsButton, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import ResponseSourceBadge from './response-source-badge';

export const FREEFORM_PAGE_SIZE = 50;

interface PaginatedFreeformListProps {
  responses: FreeformAnswer[];
  emptyMessage: string;
}

function PaginatedFreeformList({
  responses,
  emptyMessage,
}: PaginatedFreeformListProps): ReactElement {
  const [visibleCount, setVisibleCount] = useState(FREEFORM_PAGE_SIZE);
  const visibleResponses = useMemo(
    () => responses.slice(0, visibleCount),
    [responses, visibleCount]
  );
  const hasMore = visibleCount < responses.length;

  if (responses.length === 0) {
    return (
      <p className={portalDs.bodySm}>
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <ul
        className="max-h-80 space-y-2 overflow-y-auto"
        aria-label="Freeform responses"
      >
        {visibleResponses.map((entry, entryIndex) => (
          <li
            key={`${entry.userType}-${entry.answer}-${entryIndex}`}
            className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] px-3 py-2.5"
          >
            <p className={cn(portalDs.bodySm, 'text-[#0D0F1A]')}>{entry.answer}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <ResponseSourceBadge userType={entry.userType} />
              {entry.createdAt ? (
                <span className={portalDs.caption}>{formatDisplayDateTime(entry.createdAt)}</span>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {hasMore ? (
        <PortalDsButton
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setVisibleCount((current) => current + FREEFORM_PAGE_SIZE)}
        >
          Show more (
          {responses.length - visibleCount}
          {' '}
          remaining)
        </PortalDsButton>
      ) : null}
    </div>
  );
}

export default PaginatedFreeformList;
